import React from 'react';
import {useIntl} from 'react-intl';
import {dispatch, EVENT_ID} from '../../../../containers/event_manager';
import {anchorElementId, novaInstanceKey, NovaAgentPicker, type NovaAgentPickerAlign} from './NovaAgentPicker';
import {showChangeAgentConfirmationModal} from './modals/ChangeAgentConfirmationModal';
import {NovaConversationActionButton} from './NovaCompactButton';

export interface ChangeAgentAffordanceProps {
	taskId: string;
	// See NovaAgentPickerProps.instanceKey (NovaAgentPicker.tsx).
	instanceKey?: string;
	currentAgentId: string;
	hasReplies: boolean;
	onAgentChanged: (newAgentId: string) => void;
	// See NovaAgentPickerProps.anchorInstanceKey: which control the list hangs from, when that is
	// not this button. The design opens it under the task modal's own Action/Continue control.
	anchorInstanceKey?: string;
	// How the list lines up with that control — 'right' where it sits at the edge of the modal.
	align?: NovaAgentPickerAlign;
	// Shown, but refused, with the reason on it — changing the agent starts a fresh run, so it
	// needs the same permission acting on the task needs.
	disabled?: boolean;
	disabledReason?: string;
}

export const ChangeAgentAffordance = (props: ChangeAgentAffordanceProps): JSX.Element => {
	const {formatMessage} = useIntl();

	const handleSelect = (selectedAgentId: string): void => {
		if (selectedAgentId === props.currentAgentId) return;
		if (props.hasReplies) {
			showChangeAgentConfirmationModal({onConfirm: () => props.onAgentChanged(selectedAgentId)});
		} else {
			props.onAgentChanged(selectedAgentId);
		}
	};

	return (
		<>
			{/* The same compact outlined control the answer card's other actions use, in its neutral
			    tone: changing the agent is not the card's primary act, and a text link among three
			    bordered buttons reads as a different kind of thing than it is. */}
			<NovaConversationActionButton
				tone="neutral"
				onPress={() => dispatch(EVENT_ID.NOVA_TOGGLE_PICKER, novaInstanceKey(props.taskId, props.instanceKey))}
				disabled={props.disabled}
				title={props.disabled ? props.disabledReason : undefined}
				data-cy="nova-change-agent"
				id={anchorElementId(props.taskId, props.instanceKey)}
			>
				{formatMessage({id: 'nova_invocation.change_agent.button'})}
			</NovaConversationActionButton>
			<NovaAgentPicker
				taskId={props.taskId}
				instanceKey={props.instanceKey}
				anchorInstanceKey={props.anchorInstanceKey}
				align={props.align}
				onSelectAgent={handleSelect}
			/>
		</>
	);
};
